// src/components/CloverCatalogMap.jsx
//
// Collapsible card that lets restaurant owners map their TableChat dishes
// to Clover inventory items. Appears in the portal Menu page below the
// Square catalog mapping card.
//
// When a mapping is saved, future orders use the Clover item ID instead
// of free-form line items — inventory decrements and sales reports roll
// up correctly by category (Option A accuracy upgrade).

import React, { useState, useEffect } from 'react';
import { portalFetch } from '../lib/supabase';

function fmtMoney(n) { return n != null ? `$${parseFloat(n).toFixed(2)}` : ''; }

export default function CloverCatalogMap({ categories, restaurant }) {
  const [open, setOpen]                 = useState(false);
  const [cloverItems, setCloverItems]   = useState([]);
  const [mappings, setMappings]         = useState({});  // { dishId: cloverItemId }
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]               = useState(false);
  const [error, setError]               = useState('');
  const [catalogError, setCatalogError] = useState('');

  const allDishes = (categories || []).flatMap(cat =>
    (cat.dishes || []).map(d => ({ ...d, categoryName: cat.display_name }))
  );

  useEffect(() => {
    if (!open || cloverItems.length > 0) return;
    loadCatalog();
  }, [open]);

  const loadCatalog = async () => {
    setLoading(true);
    setCatalogError('');
    try {
      const catalogData = await portalFetch('/clover/catalog');
      if (catalogData?.error) {
        setCatalogError(catalogData.error);
        setLoading(false);
        return;
      }
      setCloverItems(catalogData?.items || []);

      // Load existing dish mappings
      const menuData = await portalFetch('/menu');
      if (menuData?.categories) {
        const existingMappings = {};
        menuData.categories.forEach(cat => {
          (cat.dishes || []).forEach(dish => {
            if (dish.clover_item_id) {
              existingMappings[dish.id] = dish.clover_item_id;
            }
          });
        });
        setMappings(existingMappings);
      }
    } catch (err) {
      setCatalogError('Failed to load Clover inventory. Check your Clover credentials in Settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (dishId, cloverItemId) => {
    setMappings(prev => ({ ...prev, [dishId]: cloverItemId }));
    setSaved(false);
  };

  const saveMappings = async () => {
    setError('');
    setSaving(true);

    const mappingArray = Object.entries(mappings).map(([dish_id, clover_item_id]) => ({
      dish_id,
      clover_item_id: clover_item_id || null,
    }));

    const result = await portalFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'clover_catalog_map',
        updates: { mappings: mappingArray },
      }),
    });

    setSaving(false);
    if (result?.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } else {
      setError(result?.error || 'Failed to save mappings. Please try again.');
    }
  };

  const mappedCount = allDishes.filter(d => mappings[d.id]).length;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, marginBottom: '1.5rem', overflow: 'hidden', background: 'white' }}>

      {/* Header */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '1rem 1.4rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
          background: open ? 'var(--warm)' : 'white',
          borderBottom: open ? '1px solid var(--border)' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
          <span style={{ fontSize: '1.1rem' }}>🔗</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
              Map to Clover inventory
              {mappedCount > 0 && (
                <span style={{ marginLeft: 8, fontSize: '0.75rem', background: '#D1FAE5', color: '#065F46', padding: '0.15rem 0.5rem', borderRadius: 10, fontWeight: 700 }}>
                  {mappedCount}/{allDishes.length} mapped
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
              Link dishes to your Clover inventory for accurate stock and sales reporting
            </div>
          </div>
        </div>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{open ? '▲' : '▾'}</span>
      </div>

      {open && (
        <div style={{ padding: '1.2rem 1.4rem' }}>

          {/* Info banner */}
          <div style={{ background: '#E6F1FB', border: '1px solid #B5D4F4', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1.2rem', fontSize: '0.8rem', color: '#0C447C', lineHeight: 1.6 }}>
            <strong>Why map dishes?</strong> Unmapped dishes are sent to Clover as custom line items —
            they appear in orders but don't affect inventory or category reports.
            Mapped dishes use your Clover inventory items directly, keeping stock and reporting accurate.
            Leave a dish unmapped if it doesn't exist in your Clover inventory.
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem' }}>
              Loading Clover inventory...
            </div>
          )}

          {/* Catalog error */}
          {catalogError && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#991B1B' }}>
              {catalogError}
              <button onClick={loadCatalog} style={{ display: 'block', marginTop: 6, background: 'none', border: 'none', color: '#991B1B', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.82rem', padding: 0 }}>
                Try again
              </button>
            </div>
          )}

          {/* Empty catalog */}
          {!loading && !catalogError && cloverItems.length === 0 && (
            <div style={{ background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#795548', lineHeight: 1.6 }}>
              <strong>No items found in your Clover inventory.</strong><br />
              Add items at <strong>sandbox.dev.clover.com → Inventory</strong> (or clover.com for production),
              then come back and refresh this section.
              <button onClick={loadCatalog} style={{ display: 'block', marginTop: 6, background: 'none', border: 'none', color: '#795548', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.82rem', padding: 0 }}>
                Refresh inventory
              </button>
            </div>
          )}

          {/* Mapping table */}
          {!loading && !catalogError && cloverItems.length > 0 && allDishes.length > 0 && (
            <>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem', padding: '0 0.2rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>TableChat dish</div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Clover inventory item</div>
              </div>

              {/* Dish rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.2rem' }}>
                {allDishes.map(dish => {
                  const currentMapping = mappings[dish.id] || '';
                  const isMapped = !!currentMapping;

                  return (
                    <div
                      key={dish.id}
                      style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem',
                        alignItems: 'center', padding: '0.65rem 0.8rem',
                        background: isMapped ? '#F0FDF4' : 'var(--warm)',
                        borderRadius: 8,
                        border: `1px solid ${isMapped ? '#86EFAC' : 'var(--border)'}`,
                      }}
                    >
                      {/* Left — TableChat dish */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {dish.name}
                        </div>
                        <div style={{ fontSize: '0.73rem', color: 'var(--muted)', marginTop: 2 }}>
                          {dish.categoryName} · {fmtMoney(dish.price)}
                          {isMapped && <span style={{ marginLeft: 6, color: '#16A34A', fontWeight: 700 }}>✓ mapped</span>}
                        </div>
                      </div>

                      {/* Right — Clover item dropdown */}
                      <select
                        value={currentMapping}
                        onChange={e => handleMappingChange(dish.id, e.target.value)}
                        style={{
                          width: '100%', padding: '0.5rem 0.7rem',
                          border: `1.5px solid ${isMapped ? '#86EFAC' : 'var(--border)'}`,
                          borderRadius: 7, fontSize: '0.82rem',
                          outline: 'none', color: 'var(--ink)', background: 'white',
                          cursor: 'pointer',
                        }}
                      >
                        <option value="">— Not mapped (free-form) —</option>
                        {cloverItems.map(item => (
                          <option key={item.id} value={item.id}>
                            {item.name}{item.price ? ` · $${item.price}` : ''}{item.category ? ` · ${item.category}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              {/* Summary */}
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                {mappedCount} of {allDishes.length} dishes mapped to Clover inventory.
                {allDishes.length - mappedCount > 0 && ` ${allDishes.length - mappedCount} will use free-form line items.`}
              </div>

              {/* Error */}
              {error && (
                <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#991B1B' }}>
                  {error}
                </div>
              )}

              {/* Save button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  onClick={saveMappings}
                  disabled={saving}
                  style={{
                    background: 'var(--orange)', color: 'white', border: 'none',
                    borderRadius: 8, padding: '0.75rem 1.6rem',
                    fontWeight: 700, fontSize: '0.875rem',
                    cursor: saving ? 'default' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving...' : 'Save mappings'}
                </button>
                {saved && (
                  <span style={{ color: '#16A34A', fontWeight: 700, fontSize: '0.875rem' }}>
                    ✓ Saved — future orders will use Clover item IDs where mapped
                  </span>
                )}
              </div>
            </>
          )}

          {/* Empty dishes */}
          {!loading && !catalogError && allDishes.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem' }}>
              No dishes found. Add menu items first, then come back to map them.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
